/**
 * SRE Project Page (Premium Dark) - UNIFIED UPLOAD SYSTEM
 * =========================================================
 * Main page for the Slab Reality Engine.
 * 
 * CRITICAL: Uses UploadAndCropComponent for ALL uploads.
 * 
 * Flow:
 * 1. Upload environment image → MANDATORY CROP → Confirm
 * 2. Upload slab image → MANDATORY CROP → Confirm  
 * 3. Define surface (4-point selection)
 * 4. Generate projection → View result
 * 
 * SINGLE SOURCE OF TRUTH:
 * - ALL images go through UploadAndCropComponent
 * - Crop is NEVER skippable
 * - Output is ALWAYS slab_clean
 * 
 * Design: Premium Dark (#121212) - Technical, Precise, Elegant
 * NO AI GENERATIVA - 100% deterministic processing.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Image, Layers, Play, Download, ArrowLeft, 
  CheckCircle, Loader2, ChevronRight, RefreshCw, AlertTriangle
} from 'lucide-react';
import SurfaceCanvasV3 from '../../components/sre/SurfaceCanvasV3';
import LayoutSelector from '../../components/sre/LayoutSelector';
import UploadAndCropComponent from '../../components/sre/UploadAndCropComponent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Premium Dark Color Palette
const COLORS = {
  bg: {
    primary: '#121212',
    secondary: '#1A1A1A',
    tertiary: '#0A0A0A',
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#A0A0A0',
    muted: '#666666',
  },
  accent: {
    primary: '#D6D6D6',
    hover: '#FFFFFF',
    active: '#E8E8E8',
  },
  border: {
    subtle: '#2A2A2A',
    medium: '#3A3A3A',
  },
  status: {
    success: '#4ADE80',
    warning: '#FBBF24',
    error: '#EF4444',
  }
};

// Steps configuration
const STEPS = [
  { id: 'environment', label: 'Environment', icon: Image },
  { id: 'slab', label: 'Slab', icon: Layers },
  { id: 'surface', label: 'Surface', icon: CheckCircle },
  { id: 'result', label: 'Result', icon: CheckCircle },
];

const SREProjectPage = () => {
  const navigate = useNavigate();
  
  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [projectId, setProjectId] = useState(null);
  
  // Environment image state - ONLY slab_clean is used
  const [environmentImage, setEnvironmentImage] = useState(null); // slab_clean URL
  const [environmentImageId, setEnvironmentImageId] = useState(null);
  const [uploadingEnv, setUploadingEnv] = useState(false);
  
  // Slab image state - ONLY slab_clean is used
  const [slabImage, setSlabImage] = useState(null); // slab_clean URL
  const [slabImageId, setSlabImageId] = useState(null);
  const [uploadingSlab, setUploadingSlab] = useState(false);
  
  // Surface & processing state
  const [surfacePoints, setSurfacePoints] = useState(null);
  const [selectedPattern, setSelectedPattern] = useState('single');
  const [dimensions, setDimensions] = useState({
    surfaceWidthCm: 300,
    surfaceHeightCm: 200,
    slabWidthCm: 320,
    slabHeightCm: 180,
    groutWidthCm: 0,
  });
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Create project if not exists
  const ensureProject = async () => {
    if (projectId) return projectId;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/sre/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: `SRE Project ${new Date().toISOString().slice(0, 10)}` }),
      });
      
      if (!response.ok) throw new Error('Failed to create project');
      const data = await response.json();
      setProjectId(data.project_id);
      return data.project_id;
    } catch (err) {
      setError('Failed to create project');
      throw err;
    }
  };
  
  // =============================================
  // ENVIRONMENT UPLOAD COMPLETE HANDLER
  // =============================================
  const handleEnvironmentComplete = useCallback((result, slabCleanUrl, blob) => {
    console.log('ENVIRONMENT UPLOAD COMPLETE:', result);
    console.log('SLAB CLEAN URL:', slabCleanUrl);
    console.log('IMAGE ID:', result.image_id);
    
    // CRITICAL: Use ONLY slab_clean
    setEnvironmentImage(slabCleanUrl);
    setEnvironmentImageId(result.image_id);
    setCurrentStep(1);
    setUploadingEnv(false);
  }, []);
  
  // =============================================
  // SLAB UPLOAD COMPLETE HANDLER  
  // =============================================
  const handleSlabComplete = useCallback((result, slabCleanUrl, blob) => {
    console.log('SLAB UPLOAD COMPLETE:', result);
    console.log('SLAB CLEAN URL:', slabCleanUrl);
    console.log('IMAGE ID:', result.image_id);
    
    // CRITICAL: Use ONLY slab_clean
    setSlabImage(slabCleanUrl);
    setSlabImageId(result.image_id);
    setCurrentStep(2);
    setUploadingSlab(false);
  }, []);
  
  // Handle cancel - go back
  const handleUploadCancel = useCallback(() => {
    // Reset to idle state
    if (currentStep === 0) {
      navigate(-1);
    } else {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, navigate]);
  
  // Handle surface points change
  const handlePointsChange = useCallback((points) => {
    setSurfacePoints(points);
  }, []);
  
  // Handle dimension changes
  const handleDimensionChange = useCallback((changes) => {
    setDimensions(prev => ({ ...prev, ...changes }));
  }, []);
  
  // =============================================
  // PROCESS PROJECTION - USES ONLY slab_clean
  // =============================================
  const handleProcess = async () => {
    // HARD VALIDATION: slab_clean required
    if (!environmentImageId) {
      setError('SLAB_CLEAN_REQUIRED - Environment image not ready');
      return;
    }
    if (!slabImageId) {
      setError('SLAB_CLEAN_REQUIRED - Slab texture not ready');
      return;
    }
    if (!surfacePoints?.points || surfacePoints.points.length < 4) {
      setError('Please define all 4 surface corners');
      return;
    }
    
    setProcessing(true);
    setError(null);
    
    // Log what we're using
    console.log('RENDER - Environment Image ID:', environmentImageId);
    console.log('RENDER - Slab Image ID:', slabImageId);
    console.log('RENDER - Using SLAB_CLEAN only');
    
    try {
      const corners = surfacePoints.points.map(p => ({ x: p[0], y: p[1] }));
      
      const response = await fetch(`${BACKEND_URL}/api/sre/v2/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slab_image_id: slabImageId,
          environment_image_id: environmentImageId,
          surface: {
            name: 'Main Surface',
            corners_px: corners,
            real_dimensions: {
              width_cm: dimensions.surfaceWidthCm,
              height_cm: dimensions.surfaceHeightCm,
            },
          },
          slab_dimensions: {
            width_cm: dimensions.slabWidthCm,
            height_cm: dimensions.slabHeightCm,
          },
          layout: {
            pattern: selectedPattern,
            grout_width_cm: dimensions.groutWidthCm,
            grout_color: '#808080',
          },
          options: {
            opacity: 1.0,
            output_quality: 95,
          },
        }),
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Processing failed');
      }
      
      const data = await response.json();
      
      console.log('RENDER RESULT:', data);
      
      if (data.success) {
        setResult(data);
        setCurrentStep(3);
        
        // Track completion
        try {
          await fetch(`${BACKEND_URL}/api/sre/analytics/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              event: 'sre_project_completed',
              metadata: { pattern: selectedPattern }
            })
          });
        } catch (e) { /* silent */ }
      } else {
        throw new Error(data.error || 'Processing failed');
      }
    } catch (err) {
      setError(`Processing failed: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };
  
  // =============================================
  // RENDER STEP CONTENT
  // =============================================
  const renderStepContent = () => {
    switch (currentStep) {
      // =============================================
      // STEP 0: ENVIRONMENT UPLOAD (UNIFIED)
      // =============================================
      case 0:
        return (
          <div className="space-y-8">
            <div className="text-center max-w-xl mx-auto">
              <h2 className="text-2xl font-light text-white mb-3 tracking-tight">
                Upload Environment Image
              </h2>
              <p className="text-[#A0A0A0] text-sm leading-relaxed">
                Select a photo of the space where you want to visualize the stone.
              </p>
              <p className="text-[#666666] text-xs mt-2">
                You will crop the image in the next step • Crop is mandatory
              </p>
            </div>
            
            {!environmentImage ? (
              <div className="max-w-2xl mx-auto">
                <UploadAndCropComponent
                  mode="room"
                  projectId={projectId}
                  onComplete={handleEnvironmentComplete}
                  onCancel={handleUploadCancel}
                />
              </div>
            ) : (
              <div className="max-w-2xl mx-auto space-y-4">
                <div className="relative rounded-lg overflow-hidden border border-[#2A2A2A]">
                  <img
                    src={environmentImage}
                    alt="Environment (slab_clean)"
                    className="w-full max-h-[400px] object-contain bg-[#0A0A0A]"
                  />
                  <div className="absolute top-2 left-2 px-2 py-1 bg-[#4ADE80]/20 border border-[#4ADE80]/50 rounded text-xs text-[#4ADE80]">
                    slab_clean ✓
                  </div>
                </div>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => {
                      setEnvironmentImage(null);
                      setEnvironmentImageId(null);
                    }}
                    className="px-4 py-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] text-white rounded transition-colors"
                  >
                    Change Image
                  </button>
                  <button
                    onClick={() => setCurrentStep(1)}
                    disabled={!environmentImageId}
                    className="px-6 py-2 bg-[#D6D6D6] hover:bg-white text-[#121212] font-medium rounded transition-colors flex items-center gap-2 disabled:opacity-50"
                    data-testid="sre-env-continue"
                  >
                    Continue <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      
      // =============================================
      // STEP 1: SLAB UPLOAD (UNIFIED)
      // =============================================
      case 1:
        return (
          <div className="space-y-8">
            <div className="text-center max-w-xl mx-auto">
              <h2 className="text-2xl font-light text-white mb-3 tracking-tight">
                Upload Slab Texture
              </h2>
              <p className="text-[#A0A0A0] text-sm leading-relaxed">
                Select a high-quality photo of the stone slab.
              </p>
              <p className="text-[#666666] text-xs mt-2">
                You will crop to isolate the texture • Crop is mandatory
              </p>
            </div>
            
            {!slabImage ? (
              <div className="max-w-2xl mx-auto">
                <UploadAndCropComponent
                  mode="slab"
                  projectId={projectId}
                  onComplete={handleSlabComplete}
                  onCancel={handleUploadCancel}
                />
              </div>
            ) : (
              <div className="max-w-2xl mx-auto space-y-4">
                <div className="relative rounded-lg overflow-hidden border border-[#2A2A2A]">
                  <img
                    src={slabImage}
                    alt="Slab (slab_clean)"
                    className="w-full max-h-[300px] object-contain bg-[#0A0A0A]"
                  />
                  <div className="absolute top-2 left-2 px-2 py-1 bg-[#4ADE80]/20 border border-[#4ADE80]/50 rounded text-xs text-[#4ADE80]">
                    slab_clean ✓
                  </div>
                </div>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => {
                      setSlabImage(null);
                      setSlabImageId(null);
                    }}
                    className="px-4 py-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] text-white rounded transition-colors"
                  >
                    Change Image
                  </button>
                  <button
                    onClick={() => setCurrentStep(2)}
                    disabled={!slabImageId}
                    className="px-6 py-2 bg-[#D6D6D6] hover:bg-white text-[#121212] font-medium rounded transition-colors flex items-center gap-2 disabled:opacity-50"
                    data-testid="sre-slab-continue"
                  >
                    Continue <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      
      // =============================================
      // STEP 2: SURFACE DEFINITION
      // =============================================
      case 2:
        return (
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center max-w-xl mx-auto">
              <h2 className="text-2xl font-light text-white mb-3 tracking-tight">
                Define Surface
              </h2>
              <p className="text-[#A0A0A0] text-sm leading-relaxed">
                We'll auto-detect surfaces for you, or click 4 corners manually.
                <br />
                <span className="text-[#666666]">The slab preview appears instantly.</span>
              </p>
            </div>
            
            {/* Main Canvas with Auto-Detection + Live Preview */}
            <div className="max-w-5xl mx-auto">
              <SurfaceCanvasV3
                environmentImageUrl={environmentImage}
                environmentImageId={environmentImageId}
                slabImageUrl={slabImage}
                onPointsChange={handlePointsChange}
                width={960}
                height={580}
                editable={true}
                previewOpacity={0.85}
                autoDetect={true}
              />
            </div>
            
            {/* Controls Panel */}
            <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Layout Pattern */}
              <div className="lg:col-span-2">
                <LayoutSelector
                  selectedPattern={selectedPattern}
                  onPatternChange={setSelectedPattern}
                  surfaceWidthCm={dimensions.surfaceWidthCm}
                  surfaceHeightCm={dimensions.surfaceHeightCm}
                  slabWidthCm={dimensions.slabWidthCm}
                  slabHeightCm={dimensions.slabHeightCm}
                  groutWidthCm={dimensions.groutWidthCm}
                  onDimensionsChange={handleDimensionChange}
                />
              </div>
              
              {/* Action Panel */}
              <div className="space-y-4">
                {/* Slab Preview Thumbnail */}
                {slabImage && (
                  <div className="p-4 bg-[#1A1A1A] rounded border border-[#2A2A2A]">
                    <h3 className="text-xs font-medium text-[#666666] uppercase tracking-wide mb-2">
                      Selected Slab (slab_clean)
                    </h3>
                    <img
                      src={slabImage}
                      alt="Slab"
                      className="w-full h-20 object-cover rounded"
                    />
                  </div>
                )}
                
                {/* Generate Button */}
                <button
                  onClick={handleProcess}
                  disabled={!surfacePoints?.points || surfacePoints.points.length < 4 || processing}
                  className={`
                    w-full py-4 rounded font-medium flex items-center justify-center gap-2 transition-all
                    ${surfacePoints?.points?.length === 4 && !processing
                      ? 'bg-[#D6D6D6] hover:bg-white text-[#121212]'
                      : 'bg-[#2A2A2A] text-[#666666] cursor-not-allowed'
                    }
                  `}
                  data-testid="sre-process-button"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      Generate Projection
                    </>
                  )}
                </button>
                
                {surfacePoints?.points?.length < 4 && (
                  <p className="text-[#666666] text-xs text-center">
                    Define all 4 corners to enable
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      
      // =============================================
      // STEP 3: RESULT
      // =============================================
      case 3:
        return (
          <div className="space-y-8">
            <div className="text-center max-w-xl mx-auto">
              <h2 className="text-2xl font-light text-white mb-3">Projection Complete</h2>
              <p className="text-[#A0A0A0] text-sm">
                100% deterministic – no AI generation, pure geometric transformation
              </p>
            </div>
            
            {result && (
              <>
                {/* Result image */}
                <div className="max-w-4xl mx-auto rounded-lg overflow-hidden border border-[#2A2A2A]">
                  <img
                    src={result.output?.url}
                    alt="Projection result"
                    className="w-full bg-[#0A0A0A]"
                    data-testid="sre-result-image"
                  />
                </div>
                
                {/* Metrics */}
                <div className="max-w-3xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A] text-center">
                    <div className="text-2xl font-light text-white">
                      {result.materials?.summary?.slabs_needed || 0}
                    </div>
                    <div className="text-xs text-[#A0A0A0] uppercase tracking-wide">Slabs</div>
                  </div>
                  <div className="p-4 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A] text-center">
                    <div className="text-2xl font-light text-white">
                      {result.materials?.summary?.surface_area_m2?.toFixed(2) || 0} m²
                    </div>
                    <div className="text-xs text-[#A0A0A0] uppercase tracking-wide">Area</div>
                  </div>
                  <div className="p-4 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A] text-center">
                    <div className="text-2xl font-light text-[#FBBF24]">
                      {result.materials?.summary?.waste_percent?.toFixed(1) || 0}%
                    </div>
                    <div className="text-xs text-[#A0A0A0] uppercase tracking-wide">Waste</div>
                  </div>
                  <div className="p-4 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A] text-center">
                    <div className="text-2xl font-light text-[#4ADE80]">
                      {result.fidelity?.fidelity_score?.toFixed(2) || 1.00}
                    </div>
                    <div className="text-xs text-[#A0A0A0] uppercase tracking-wide">Fidelity</div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="px-5 py-2.5 bg-[#2A2A2A] hover:bg-[#3A3A3A] text-white rounded flex items-center gap-2 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Modify
                  </button>
                  <a
                    href={result.output?.url}
                    download="sre-projection.jpg"
                    className="px-5 py-2.5 bg-[#D6D6D6] hover:bg-white text-[#121212] font-medium rounded flex items-center gap-2 transition-colors"
                    data-testid="sre-download-button"
                  >
                    <Download className="w-4 h-4" />
                    Download Result
                  </a>
                </div>
              </>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };
  
  // Ensure project is created when component mounts
  React.useEffect(() => {
    ensureProject();
  }, []);
  
  return (
    <div className="min-h-screen bg-[#121212]" data-testid="sre-project-page">
      {/* Header */}
      <header className="border-b border-[#2A2A2A] bg-[#121212]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-[#2A2A2A] rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-[#A0A0A0]" />
            </button>
            <div>
              <h1 className="text-lg font-medium text-white tracking-tight">Slab Reality Engine</h1>
              <p className="text-xs text-[#666666]">Deterministic Projection • slab_clean only</p>
            </div>
          </div>
          
          {/* Step indicator */}
          <div className="flex items-center gap-1">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isComplete = index < currentStep;
              
              return (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => isComplete && setCurrentStep(index)}
                    disabled={!isComplete}
                    className={`
                      px-3 py-2 rounded flex items-center gap-2 transition-colors
                      ${isActive 
                        ? 'bg-[#2A2A2A] text-white' 
                        : isComplete 
                          ? 'text-[#A0A0A0] hover:bg-[#2A2A2A] cursor-pointer' 
                          : 'text-[#666666]'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-medium hidden sm:inline">{step.label}</span>
                  </button>
                  {index < STEPS.length - 1 && (
                    <div className={`w-6 h-px mx-1 ${isComplete ? 'bg-[#A0A0A0]' : 'bg-[#2A2A2A]'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Error display */}
        {error && (
          <div className="mb-8 p-4 bg-[#1A1A1A] border border-[#EF4444]/30 rounded-lg flex items-center gap-3 text-[#EF4444] text-sm">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}
        
        {renderStepContent()}
      </main>
    </div>
  );
};

export default SREProjectPage;
